/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import {
  font,
  paragraphLarge,
  paragraphMedium,
  weightMedium,
} from 'src/components/css'

interface GridEmptyStateProps {
  icon: ReactNode
  title: string
  hint?: string
}

/**
 * Empty state for the operations grids — circular icon badge, title, hint.
 * Mirrors the family-management modal's treatment so both read the same.
 */
const GridEmptyState = ({ icon, title, hint }: GridEmptyStateProps) => {
  'use memo'

  return (
    <div css={wrapStyle}>
      <div css={iconWrapStyle}>{icon}</div>
      <span css={titleStyle}>{title}</span>
      {hint ? <span css={hintStyle}>{hint}</span> : null}
    </div>
  )
}

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 40px 24px;
  text-align: center;
`

const iconWrapStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 10px;
  border-radius: 50%;
  background: ${colors.NEUTRAL_700};
  font-size: 26px;
  color: ${colors.NEUTRAL_50};
`

const titleStyle = css`
  ${paragraphLarge}
  ${weightMedium}
  ${font.body}
  color: ${colors.NEUTRAL_0};
`

const hintStyle = css`
  ${paragraphMedium}
  ${font.body}
  color: ${colors.NEUTRAL_50};
`

export default GridEmptyState
